import { useEffect, useRef, useState } from 'react';
import type { Segment } from '../api.ts';

const BOXES = {
  'visste-du': { label: 'Visste du?', icon: '💡', className: 'bg-amber-50 ring-amber-200' },
  reflektion: { label: 'Fundera på', icon: '💭', className: 'bg-violet-50 ring-violet-200' },
  fordjupning: { label: 'Vill du veta mer?', icon: '🔭', className: 'bg-aurora-100/50 ring-aurora-400/40' },
  transkript: { label: 'Transkript', icon: '', className: '' },
} as const;

const clock = (seconds: number) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = String(seconds % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

/** Visar renderat kursinnehåll: HTML (redan sanerad av servern), YouTube-filmer, ljud och rutor. */
export function Segments({
  segments,
  onWatched,
  onAudioEnded,
}: {
  segments: Segment[];
  onWatched?: () => void;
  onAudioEnded?: () => void;
}) {
  // Kapitel spolar den närmaste filmen ovanför.
  const players = useRef(new Map<number, YTPlayer>());
  const videoBefore = (index: number) => {
    for (let i = index - 1; i >= 0; i--) if (segments[i].type === 'youtube') return i;
    return -1;
  };
  return (
    <div className="prose-aurora">
      {segments.map((s, i) => {
        if (s.type === 'html')
          return <div key={i} dangerouslySetInnerHTML={{ __html: s.html }} className="prose-aurora" />;
        if (s.type === 'youtube')
          return (
            <YouTube
              key={i}
              id={s.id}
              onWatched={onWatched}
              onPlayer={(p) => (p ? players.current.set(i, p) : players.current.delete(i))}
            />
          );
        if (s.type === 'chapters') {
          const video = videoBefore(i);
          return (
            <nav key={i} aria-label="Kapitel i filmen" className="my-3">
              <ol className="!my-0 flex !list-none flex-wrap gap-2 !pl-0 text-sm">
                {s.items.map((c) => (
                  <li key={c.seconds} className="!my-0 !pl-0">
                    <button
                      type="button"
                      className="rounded-full bg-night-50 px-3 py-1 text-night-800 ring-1 ring-night-800/10 hover:bg-night-100"
                      onClick={() => {
                        const p = players.current.get(video);
                        if (p) {
                          p.seekTo(c.seconds, true);
                          p.playVideo();
                        }
                      }}
                    >
                      <span className="mr-1.5 font-mono text-xs text-muted">{clock(c.seconds)}</span>
                      {c.label}
                    </button>
                  </li>
                ))}
              </ol>
            </nav>
          );
        }
        if (s.type === 'box') {
          const box = BOXES[s.variant];
          if (s.variant === 'transkript') {
            return (
              <details key={i} className="my-4 rounded-xl bg-night-50 px-4 py-3">
                <summary className="cursor-pointer font-semibold text-night-800">
                  {s.title ?? 'Visa transkript'}
                </summary>
                <div className="mt-2 text-sm" dangerouslySetInnerHTML={{ __html: s.html }} />
              </details>
            );
          }
          return (
            <aside key={i} className={`my-5 rounded-xl p-4 ring-1 ${box.className}`}>
              <p className="!mt-0 font-bold text-night-900">
                <span aria-hidden="true">{box.icon} </span>
                {s.title ?? box.label}
              </p>
              <div className="[&>*:last-child]:mb-0" dangerouslySetInnerHTML={{ __html: s.html }} />
            </aside>
          );
        }
        return (
          <audio key={i} controls preload="metadata" src={s.src} onEnded={onAudioEnded} className="w-full">
            Din webbläsare kan inte spela upp ljudet. <a href={s.src}>Ladda ned ljudfilen</a>.
          </audio>
        );
      })}
    </div>
  );
}

// ---------- YouTube ----------

interface YTPlayer {
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  playVideo(): void;
  getCurrentTime(): number;
  getDuration(): number;
  destroy(): void;
}
declare global {
  interface Window {
    YT?: {
      Player: new (el: HTMLElement, opts: unknown) => YTPlayer;
      PlayerState: { PLAYING: number; ENDED: number };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

let apiPromise: Promise<void> | null = null;
function loadYouTubeApi(): Promise<void> {
  if (window.YT?.Player) return Promise.resolve();
  apiPromise ??= new Promise((resolve) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      resolve();
    };
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(script);
  });
  return apiPromise;
}

/**
 * Spelaren laddas direkt (via youtube-nocookie.com) men startar inte förrän deltagaren trycker
 * på play. När minst 80 % har setts anropas onWatched så att delen kan markeras som klar.
 */
function YouTube({
  id,
  onWatched,
  onPlayer,
}: {
  id: string;
  onWatched?: () => void;
  onPlayer?: (player: YTPlayer | null) => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const reported = useRef(false);
  // Senaste callbacken utan att spelaren skapas om när sidan uppdateras.
  const watched = useRef(onWatched);
  watched.current = onWatched;
  const register = useRef(onPlayer);
  register.current = onPlayer;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!host.current) return;
    let player: YTPlayer | null = null;
    let timer: number | undefined;
    let cancelled = false;
    reported.current = false;
    const report = () => {
      if (reported.current) return;
      reported.current = true;
      watched.current?.();
    };
    const fallback = window.setTimeout(() => {
      if (!player) setFailed(true);
    }, 8000);
    void loadYouTubeApi().then(() => {
      if (cancelled || !host.current || !window.YT) return;
      const target = document.createElement('div');
      host.current.replaceChildren(target);
      player = new window.YT.Player(target, {
        host: 'https://www.youtube-nocookie.com',
        videoId: id,
        width: '100%',
        height: '100%',
        playerVars: { rel: 0, modestbranding: 1, playsinline: 1, hl: 'sv', cc_lang_pref: 'sv' },
        events: {
          onReady: () => {
            window.clearTimeout(fallback);
            register.current?.(player);
          },
          onStateChange: (e: { data: number }) => {
            window.clearInterval(timer);
            if (e.data === window.YT!.PlayerState.ENDED) report();
            if (e.data === window.YT!.PlayerState.PLAYING) {
              timer = window.setInterval(() => {
                const d = player?.getDuration() ?? 0;
                if (d > 0 && (player?.getCurrentTime() ?? 0) / d >= 0.8) report();
              }, 4000);
            }
          },
        },
      });
    });
    return () => {
      cancelled = true;
      window.clearTimeout(fallback);
      window.clearInterval(timer);
      register.current?.(null);
      player?.destroy();
    };
  }, [id]);

  if (failed) {
    // Om spelar-API:t blockeras (t.ex. av ett nätverksfilter) visas en vanlig inbäddning.
    return (
      <div className="my-4 aspect-video w-full overflow-hidden rounded-xl bg-black shadow-lg">
        <iframe
          title="Film"
          src={`https://www.youtube-nocookie.com/embed/${id}?rel=0`}
          className="h-full w-full"
          allow="encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <div className="my-4 aspect-video w-full overflow-hidden rounded-xl bg-night-950 shadow-lg ring-1 ring-night-800/10">
      <div ref={host} className="h-full w-full" />
    </div>
  );
}
